/**
 * Regras da seção "Realizações Destaque excluídas" (spec `realizacoes-excluidas`).
 *
 * A página lê as excluídas na mesma carga das cadastradas, mas só em fase mensal. A leitura nunca rejeita:
 * uma falha vira `failed` e a seção mostra a mensagem de erro, sem derrubar a lista principal. O que a
 * seção mostra sai de `resolveDeletedSection`, na ordem de prioridade dos estados.
 */

import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { DeletedTask } from 'services/dashboard.service';

const SAO_PAULO = 'America/Sao_Paulo';

export interface DeletedStage {
  type?: string;
  date_start?: unknown;
  date_finish?: unknown;
}

export interface DeletedTasksResult {
  items: DeletedTask[];
  failed: boolean;
}

export type DeletedSectionState = 'oculta' | 'lista' | 'erro';

function isMonthWithWindow(stage?: DeletedStage | null): stage is DeletedStage & { date_start: number; date_finish: number } {
  return stage?.type === 'month' && typeof stage.date_start === 'number' && typeof stage.date_finish === 'number';
}

export async function loadDeletedTasks(
  stage: DeletedStage | null | undefined,
  fetcher: (start: number, end: number) => Promise<DeletedTask[]>,
): Promise<DeletedTasksResult> {
  if (!isMonthWithWindow(stage)) return { items: [], failed: false };

  try {
    return { items: await fetcher(stage.date_start, stage.date_finish), failed: false };
  } catch (error) {
    console.error('Não foi possível carregar as realizações excluídas.', error);
    return { items: [], failed: true };
  }
}

export interface DeletedSectionInput {
  tab: 'cadastrada' | 'participando';
  stage?: DeletedStage | null;
  loading: boolean;
  result?: DeletedTasksResult;
}

export function resolveDeletedSection({ tab, stage, loading, result }: DeletedSectionInput): DeletedSectionState {
  if (tab !== 'cadastrada' || stage?.type !== 'month' || loading || !result) return 'oculta';
  if (result.failed) return 'erro';

  return result.items.length > 0 ? 'lista' : 'oculta';
}

function inSaoPaulo(ms: number): Date {
  return utcToZonedTime(ms, SAO_PAULO);
}

/** Dia "dd/MM/yyyy" no horário de São Paulo: cadastro e exclusão dos cartões da Realização Destaque. */
export function formatDayInSaoPaulo(ms: number): string {
  return format(inSaoPaulo(ms), 'dd/MM/yyyy');
}

export function deletedTitle(task: DeletedTask): string {
  return task.title?.trim() || 'Realização sem título';
}

export function deletionReason(task: DeletedTask): string | null {
  return task.deletion?.reason?.trim() || null;
}
